/**
 * Shared schedule publish/unpublish validation (KIWI-6576). Ported from the Pages rules in
 * `EditPageContainer.checkScheduleValidity`, minus the toasting and loading-state side effects that
 * made that version impossible to reuse.
 *
 * The 5-minute lead time also absorbs the 3-5 minute delay the backend scheduler adds before a
 * trigger fires, so a schedule set at the minimum does not surprise the editor too badly.
 */
export type ScheduleValue = {
  isSchedulePublish: boolean;
  schedulePublishDateTime: string | null;
  isScheduleUnpublish: boolean;
  scheduleUnpublishDateTime: string | null;
};

export const EMPTY_SCHEDULE: ScheduleValue = {
  isSchedulePublish: false,
  schedulePublishDateTime: null,
  isScheduleUnpublish: false,
  scheduleUnpublishDateTime: null,
};

/** A schedule must be at least this far ahead of "now". */
export const MIN_SCHEDULE_LEAD_MINUTES = 5;
/** An unpublish must come at least this long after a scheduled publish. */
export const MIN_PUBLISH_TO_UNPUBLISH_MINUTES = 10;

export type ScheduleValidationResult =
  | { valid: true }
  | { valid: false; field: "publish" | "unpublish"; messageKey: string };

const MS_PER_MINUTE = 60_000;

const toTime = (value?: string | null): number => (value ? new Date(value).getTime() : Number.NaN);

/**
 * Validates a schedule. Returns the first problem found, publish before unpublish, so the caller can
 * show one message at a time. `now` is injectable to keep tests deterministic.
 */
export const validateSchedule = (
  value: ScheduleValue,
  now: Date = new Date(),
): ScheduleValidationResult => {
  const earliest = now.getTime() + MIN_SCHEDULE_LEAD_MINUTES * MS_PER_MINUTE;
  const publishAt = toTime(value.schedulePublishDateTime);
  const unpublishAt = toTime(value.scheduleUnpublishDateTime);

  if (value.isSchedulePublish) {
    // The backend rejects an empty time with "Schedule date-time cannot be null!".
    if (Number.isNaN(publishAt)) {
      return { valid: false, field: "publish", messageKey: "schedule_time_required" };
    }
    if (publishAt < earliest) {
      return { valid: false, field: "publish", messageKey: "publish_time_toast" };
    }
  }

  if (value.isScheduleUnpublish) {
    if (Number.isNaN(unpublishAt)) {
      return { valid: false, field: "unpublish", messageKey: "schedule_time_required" };
    }
    if (unpublishAt < earliest) {
      return { valid: false, field: "unpublish", messageKey: "unpublish_time_toast" };
    }
    if (value.isSchedulePublish && !Number.isNaN(publishAt)) {
      const earliestUnpublish = publishAt + MIN_PUBLISH_TO_UNPUBLISH_MINUTES * MS_PER_MINUTE;
      if (unpublishAt < earliestUnpublish) {
        return { valid: false, field: "unpublish", messageKey: "publish_vs_unpublish_toast" };
      }
    }
  }

  return { valid: true };
};
